'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { InitialUserData } from '@/src/lib/api/endpoints/user';
import { userKeys } from '@/src/lib/hooks/query';
import { setHotpadsComscoreWindowParams } from '@/src/lib/comscore/windowParams';
import {
  type BrowserIdentityState,
  getHotpadsIdentitySignal,
  resolveHotpadsIdentity,
} from '@/src/lib/identity/browserIdentity';
import { recordIdentityTelemetry } from '@/src/lib/identity/identityTelemetry';

export function IdentityResultCoordinator({
  identity,
  user,
}: {
  identity: BrowserIdentityState;
  user: InitialUserData | null;
}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const canCommitStreamedIdentity = queryClient.getQueryData(userKeys.info) === undefined;
    if (canCommitStreamedIdentity) {
      queryClient.setQueryData(userKeys.info, user);
      const acceptedIdentity = resolveHotpadsIdentity(identity);
      if (acceptedIdentity) {
        setHotpadsComscoreWindowParams({
          userToken: identity.userToken,
          loggedIn: identity.status === 'authenticated',
        });
      }
    }

    const signal = getHotpadsIdentitySignal();
    if (!signal || signal.reported) return;

    signal.reported = true;
    const durationMs = performance.now() - signal.startedAt;
    recordIdentityTelemetry({
      status: signal.current?.status ?? identity.status,
      durationMs,
    });
  }, [identity, queryClient, user]);

  return null;
}
